// src/pages/DailyLabor.js — TECH DAILY PAY
//
// One saved record per day: who worked, how many hours, what their day cost,
// and which jobs they were on. Everything is prefilled from the app:
//   * Worked + Hours  <- Clock In / Clock Out (time_logs, lunch excluded)
//   * Jobs            <- job check-ins (job_visits) and jobs the tech is
//                        assigned to (techAssign) that day
//   * Pay             <- Staff page rate (see utils/techDailyPay.js):
//                        flat day rate, else hourly rate x hours worked
//                        (or x Paid Hours Per Day when there are no clock hours)
// You can change any of it, then press Save Day.
//
// Saved to dailyLabor/{YYYY-MM-DD} (shape documented in utils/techDailyPay.js).
// Read by: Job Estimate Spreadsheet (actual labor per job), Expenses (payroll),
// Cash Plan (actual vs projected payroll), Profit & Profit Sharing (payroll
// check), Accounting Exports (Tech Daily Pay sheets) and Financials.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebaseConfig";
import {
  DAILY_LABOR,
  UNASSIGNED_JOB,
  fetchDailyLaborDays,
  getDefaultDailyPay,
  getHourlyPayRate,
  getHoursPerDay,
  getPayForHours,
  isDeactivated,
  isSubContractor,
  numberValue,
  roundMoney,
  staffName,
  summarizeDailyLabor,
  usersToMap,
} from "../utils/techDailyPay";
import { getOccurrencesForJob } from "../utils/getOccurrencesForJob";
import {
  isRecurring,
  makeOccurrenceDateMatcher,
  toDateKey,
} from "../utils/recurringOccurrenceUtils";

const money = (value) =>
  `$${numberValue(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const roundHours = (hours) => Math.round(numberValue(hours) * 4) / 4;

const toJsDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === "object" && typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const normName = (value = "") => String(value).toLowerCase().replace(/\s+/g, " ").trim();

const jobLabel = (job = {}) =>
  [job.wo ? `WO ${job.wo}` : "", job.siteName || job.propertyName || ""].filter(Boolean).join(" — ") ||
  job.id;

// Does this job have a visit on `dateKey`?
const jobRunsOn = (job, dateKey) => {
  if (!job || !dateKey) return false;
  if (isRecurring(job)) {
    const day = moment(dateKey, "YYYY-MM-DD");
    const dates = getOccurrencesForJob(job, day, day) || [];
    if (!dates.includes(dateKey)) {
      // A visit moved ONTO this day by "edit this job only".
      const overrides = job.occurrenceOverrides || {};
      return Object.values(overrides).some(
        (o) => toDateKey(o?.startDate || o?.occurrenceDate) === dateKey
      );
    }
    const isExcluded = makeOccurrenceDateMatcher(job, job.excludedDates);
    return !isExcluded(dateKey);
  }
  const start = toDateKey(job.startDate);
  const end = toDateKey(job.endDate) || start;
  return Boolean(start) && dateKey >= start && dateKey <= (end < start ? start : end);
};

const techsOn = (job = {}) =>
  (Array.isArray(job.techAssign) ? job.techAssign : [job.techAssign]).map(normName).filter(Boolean);

const box = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 16,
  marginBottom: 16,
  boxShadow: "0 1px 2px rgba(15,23,42,.05)",
};
const th = {
  textAlign: "left",
  padding: "10px 8px",
  background: "#f1f5f9",
  color: "#334155",
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: ".03em",
  borderBottom: "1px solid #e2e8f0",
  whiteSpace: "nowrap",
};
const td = { padding: "8px", borderBottom: "1px solid #f1f5f9", verticalAlign: "top" };
const inputStyle = {
  width: 90,
  padding: "6px 8px",
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  boxSizing: "border-box",
};
const btn = (bg, color = "#fff") => ({
  background: bg,
  color,
  border: "none",
  borderRadius: 8,
  padding: "8px 14px",
  fontWeight: 700,
  cursor: "pointer",
  width: "auto",
  margin: 0,
});

export default function DailyLabor() {
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dayLoading, setDayLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [showSubs, setShowSubs] = useState(false);

  // Per-day state
  const [attendance, setAttendance] = useState({});
  const [hours, setHours] = useState({});
  const [overrides, setOverrides] = useState({});
  const [assignments, setAssignments] = useState({});
  const [clockHours, setClockHours] = useState({});
  const [savedExists, setSavedExists] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const [pickerFor, setPickerFor] = useState(null);
  const [pickerSearch, setPickerSearch] = useState("");

  // Period summary
  const [summaryRange, setSummaryRange] = useState("week");
  const [rangeDays, setRangeDays] = useState([]);
  const [rangeTick, setRangeTick] = useState(0);

  // ---------------------------------------------------------------- load
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [usersSnap, jobsSnap] = await Promise.all([
          getDocs(collection(db, "users")),
          getDocs(collection(db, "jobs")),
        ]);
        setUsers(
          usersSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((u) => staffName(u))
            .sort((a, b) => staffName(a).localeCompare(staffName(b)))
        );
        setJobs(jobsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error("Tech Daily Pay load failed:", error);
        alert("Could not load staff and jobs.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const usersById = useMemo(() => usersToMap(users), [users]);
  const jobsById = useMemo(
    () => jobs.reduce((acc, j) => ({ ...acc, [j.id]: j }), {}),
    [jobs]
  );

  // Jobs with a visit on this day (shown first in the picker).
  const jobsToday = useMemo(
    () => jobs.filter((j) => j.status !== "Deleted" && jobRunsOn(j, date)),
    [jobs, date]
  );

  // Read through refs so hiding/unhiding someone (which updates `users`) does
  // NOT rebuild the suggestions and reload the day over unsaved edits.
  const usersRef = useRef(users);
  usersRef.current = users;
  const jobsTodayRef = useRef(jobsToday);
  jobsTodayRef.current = jobsToday;

  // What the app already knows about this day: clock hours + check-ins + techAssign.
  const buildSuggestions = useCallback(async () => {
    const dayStart = moment(date, "YYYY-MM-DD").startOf("day").toDate();
    const dayEnd = moment(date, "YYYY-MM-DD").endOf("day").toDate();

    const [logsSnap, visitsSnap] = await Promise.all([
      getDocs(
        query(
          collection(db, "time_logs"),
          where("clockIn", ">=", dayStart),
          where("clockIn", "<=", dayEnd)
        )
      ).catch(() => null),
      getDocs(collection(db, "job_visits")).catch(() => null),
    ]);

    const minutesByUser = {};
    (logsSnap?.docs || []).forEach((d) => {
      const log = d.data();
      if (log.isLunch) return;
      const reason = String(log.reason || "").toLowerCase();
      if (reason.includes("off") || reason.includes("payment")) return;
      const start = toJsDate(log.clockIn);
      if (!start) return;
      const end = toJsDate(log.clockOut);
      const minutes = numberValue(log.duration) || (end ? Math.round((end - start) / 60000) : 0);
      minutesByUser[log.userId] = (minutesByUser[log.userId] || 0) + Math.max(0, minutes);
    });
    // Lunch is its own (unpaid) log and was skipped above, so it is never
    // counted in the work minutes.

    const jobsByUser = {};
    (visitsSnap?.docs || []).forEach((d) => {
      const v = d.data();
      const checkIn = toJsDate(v.checkInAt);
      if (!checkIn || moment(checkIn).format("YYYY-MM-DD") !== date) return;
      if (!v.userId || !v.jobId) return;
      jobsByUser[v.userId] = Array.from(new Set([...(jobsByUser[v.userId] || []), v.jobId]));
    });

    usersRef.current.forEach((u) => {
      if (jobsByUser[u.id]?.length) return;
      const name = normName(staffName(u));
      const assigned = jobsTodayRef.current.filter((j) => techsOn(j).includes(name)).map((j) => j.id);
      if (assigned.length) jobsByUser[u.id] = assigned;
    });

    const hoursByUser = {};
    Object.entries(minutesByUser).forEach(([uid, minutes]) => {
      if (minutes > 0) hoursByUser[uid] = roundHours(minutes / 60);
    });

    return { hoursByUser, jobsByUser };
  }, [date]);

  // Load the saved day, or prefill a new one.
  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    (async () => {
      setDayLoading(true);
      setPickerFor(null);
      try {
        const [snap, suggestions] = await Promise.all([
          getDoc(doc(db, DAILY_LABOR, date)),
          buildSuggestions(),
        ]);
        if (cancelled) return;
        setClockHours(suggestions.hoursByUser);

        if (snap.exists()) {
          const d = snap.data();
          setAttendance(d.attendance || {});
          setHours(d.hours || {});
          setOverrides(d.payOverrides || {});
          setAssignments(d.assignments || {});
          setSavedExists(true);
          setLastSavedAt(toJsDate(d.updatedAt));
          setDirty(false);
        } else {
          const nextAttendance = {};
          Object.keys(suggestions.hoursByUser).forEach((uid) => {
            nextAttendance[uid] = true;
          });
          setAttendance(nextAttendance);
          setHours(suggestions.hoursByUser);
          setOverrides({});
          setAssignments(suggestions.jobsByUser);
          setSavedExists(false);
          setLastSavedAt(null);
          setDirty(Object.keys(nextAttendance).length > 0);
        }
      } catch (error) {
        console.error("Could not load the day:", error);
      } finally {
        if (!cancelled) setDayLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [date, loading, buildSuggestions]);

  // Period summary (saved days only).
  const [rangeStart, rangeEnd] = useMemo(() => {
    const d = moment(date, "YYYY-MM-DD");
    if (summaryRange === "month")
      return [d.clone().startOf("month").format("YYYY-MM-DD"), d.clone().endOf("month").format("YYYY-MM-DD")];
    return [d.clone().startOf("week").format("YYYY-MM-DD"), d.clone().endOf("week").format("YYYY-MM-DD")];
  }, [date, summaryRange]);

  useEffect(() => {
    let cancelled = false;
    fetchDailyLaborDays(rangeStart, rangeEnd)
      .then((days) => !cancelled && setRangeDays(days))
      .catch((error) => console.warn("Could not load period summary:", error));
    return () => {
      cancelled = true;
    };
  }, [rangeStart, rangeEnd, rangeTick]);

  const periodSummary = useMemo(
    () => summarizeDailyLabor(rangeDays, usersById, rangeStart, rangeEnd),
    [rangeDays, usersById, rangeStart, rangeEnd]
  );

  // ---------------------------------------------------------------- math
  const payFor = (u) => {
    const ov = overrides[u.id];
    if (ov !== undefined && ov !== null && ov !== "") return roundMoney(ov);
    const h = numberValue(hours[u.id]);
    return h > 0 ? getPayForHours(u, h) : getDefaultDailyPay(u);
  };

  const roster = useMemo(
    () =>
      users.filter((u) => {
        if (attendance[u.id]) return true; // always show anyone marked worked
        if (isDeactivated(u)) return false;
        if (!showHidden && u.hiddenFromDailyLabor) return false;
        if (!showSubs && isSubContractor(u)) return false;
        return true;
      }),
    [users, attendance, showHidden, showSubs]
  );

  const workedUsers = users.filter((u) => attendance[u.id]);
  const dayTotal = roundMoney(workedUsers.reduce((sum, u) => sum + payFor(u), 0));
  const unassignedTotal = roundMoney(
    workedUsers
      .filter((u) => !(assignments[u.id] || []).length)
      .reduce((sum, u) => sum + payFor(u), 0)
  );

  // ---------------------------------------------------------------- edits
  const touch = () => setDirty(true);

  const toggleWorked = (u) => {
    touch();
    setAttendance((prev) => ({ ...prev, [u.id]: !prev[u.id] }));
    if (!attendance[u.id] && !numberValue(hours[u.id])) {
      setHours((prev) => ({ ...prev, [u.id]: clockHours[u.id] || getHoursPerDay(u) }));
    }
  };

  const setHoursFor = (uid, value) => {
    touch();
    setHours((prev) => ({ ...prev, [uid]: value }));
  };

  const setOverrideFor = (uid, value) => {
    touch();
    setOverrides((prev) => ({ ...prev, [uid]: value }));
  };

  const toggleJob = (uid, jobId) => {
    touch();
    setAssignments((prev) => {
      const set = new Set(prev[uid] || []);
      if (set.has(jobId)) set.delete(jobId);
      else set.add(jobId);
      return { ...prev, [uid]: Array.from(set) };
    });
  };

  const toggleHidden = async (u) => {
    const next = !u.hiddenFromDailyLabor;
    await updateDoc(doc(db, "users", u.id), { hiddenFromDailyLabor: next });
    setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, hiddenFromDailyLabor: next } : x)));
  };

  const refillFromClock = async () => {
    if (
      dirty &&
      !window.confirm(
        "Replace this day's Worked / Hours / Jobs with what Clock In/Out and job check-ins show? Pay overrides are kept."
      )
    )
      return;
    const suggestions = await buildSuggestions();
    setClockHours(suggestions.hoursByUser);
    setAttendance((prev) => {
      const next = { ...prev };
      Object.keys(suggestions.hoursByUser).forEach((uid) => {
        next[uid] = true;
      });
      return next;
    });
    setHours((prev) => ({ ...prev, ...suggestions.hoursByUser }));
    setAssignments((prev) => ({ ...prev, ...suggestions.jobsByUser }));
    touch();
  };

  // ---------------------------------------------------------------- save
  const save = async () => {
    setSaving(true);
    try {
      const workers = workedUsers.map((u) => ({
        userId: u.id,
        name: staffName(u),
        worked: true,
        hours: numberValue(hours[u.id]) || getHoursPerDay(u),
        hourlyRate: roundMoney(getHourlyPayRate(u)),
        dailyPay: payFor(u),
        jobIds: (assignments[u.id] || []).filter(Boolean),
      }));

      const jobCosts = {};
      workers.forEach((w) => {
        const list = w.jobIds.length ? w.jobIds : [UNASSIGNED_JOB];
        const share = w.dailyPay / list.length;
        list.forEach((jobId) => {
          jobCosts[jobId] = roundMoney((jobCosts[jobId] || 0) + share);
        });
      });

      const cleanAttendance = {};
      workedUsers.forEach((u) => {
        cleanAttendance[u.id] = true;
      });

      const user = getAuth().currentUser;
      await setDoc(doc(db, DAILY_LABOR, date), {
        date,
        attendance: cleanAttendance,
        hours,
        payOverrides: overrides,
        assignments,
        workers,
        jobCosts,
        unassignedCost: roundMoney(jobCosts[UNASSIGNED_JOB] || 0),
        totalCost: dayTotal,
        updatedAt: serverTimestamp(),
        updatedBy: user?.email || user?.uid || "",
      });
      setSavedExists(true);
      setDirty(false);
      setLastSavedAt(new Date());
      setRangeTick((t) => t + 1);
    } catch (error) {
      console.error("Save failed:", error);
      alert("Could not save this day. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const deleteDay = async () => {
    if (!savedExists) return;
    if (
      !window.confirm(
        `Delete the saved Tech Daily Pay for ${moment(date).format("ddd, MMM D, YYYY")}?\n\nThis removes the day's labor from every Accounting page.`
      )
    )
      return;
    try {
      await deleteDoc(doc(db, DAILY_LABOR, date));
      setSavedExists(false);
      setAttendance({});
      setHours({});
      setOverrides({});
      setAssignments({});
      setDirty(false);
      setRangeTick((t) => t + 1);
    } catch (error) {
      console.error(error);
      alert("Could not delete this day.");
    }
  };

  const goDay = (delta) => {
    if (dirty && !window.confirm("You have unsaved changes for this day. Leave without saving?")) return;
    setDate((d) => moment(d, "YYYY-MM-DD").add(delta, "day").format("YYYY-MM-DD"));
  };

  const pickDate = (value) => {
    if (!value) return;
    if (dirty && !window.confirm("You have unsaved changes for this day. Leave without saving?")) return;
    setDate(value);
  };

  // ---------------------------------------------------------------- job picker
  const pickerUser = pickerFor ? usersById[pickerFor] : null;
  const pickerJobs = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    const todayIds = new Set(jobsToday.map((j) => j.id));
    const pool = q
      ? jobs.filter((j) =>
          `${j.wo || ""} ${j.siteName || ""} ${j.serviceType || ""}`.toLowerCase().includes(q)
        )
      : jobs.filter((j) => todayIds.has(j.id) || (assignments[pickerFor] || []).includes(j.id));
    return pool
      .slice()
      .sort((a, b) => Number(todayIds.has(b.id)) - Number(todayIds.has(a.id)) || jobLabel(a).localeCompare(jobLabel(b)))
      .slice(0, 80);
  }, [pickerSearch, jobs, jobsToday, assignments, pickerFor]);

  // ---------------------------------------------------------------- render
  if (loading) {
    return <div style={{ padding: 20 }}>Loading staff and jobs…</div>;
  }

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: "0 auto", textAlign: "left" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 12, marginBottom: 12 }}>
        <div style={{ flex: "1 1 260px" }}>
          <h2 style={{ margin: 0 }}>Tech Daily Pay</h2>
          <div style={{ color: "#64748b", fontSize: 14 }}>
            Who worked, hours, day pay and jobs — feeds every Accounting page.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button type="button" style={btn("#e2e8f0", "#0f172a")} onClick={() => goDay(-1)}>
            ◀
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => pickDate(e.target.value)}
            style={{ ...inputStyle, width: 160 }}
          />
          <button type="button" style={btn("#e2e8f0", "#0f172a")} onClick={() => goDay(1)}>
            ▶
          </button>
          <button
            type="button"
            style={btn("#fff7ed", "#7c2d12")}
            onClick={() => pickDate(moment().format("YYYY-MM-DD"))}
          >
            Today
          </button>
        </div>
      </div>

      <div style={{ ...box, display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 12, color: "#64748b" }}>{moment(date).format("dddd, MMM D, YYYY")}</div>
          <div style={{ fontSize: 26, fontWeight: 900 }}>{money(dayTotal)}</div>
          <div style={{ fontSize: 13, color: "#475569" }}>
            {workedUsers.length} worked
            {unassignedTotal > 0 && (
              <span style={{ color: "#b45309" }}> · {money(unassignedTotal)} not tied to a job</span>
            )}
          </div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              padding: "4px 10px",
              borderRadius: 999,
              background: dirty ? "#fef3c7" : savedExists ? "#dcfce7" : "#f1f5f9",
              color: dirty ? "#92400e" : savedExists ? "#166534" : "#475569",
            }}
          >
            {dirty
              ? "Unsaved changes"
              : savedExists
              ? `Saved${lastSavedAt ? ` ${moment(lastSavedAt).format("MMM D, h:mm a")}` : ""}`
              : "Not saved yet"}
          </span>
          <button type="button" style={btn("#2563eb")} onClick={refillFromClock} disabled={dayLoading}>
            Fill from Clock In / Check-ins
          </button>
          <button type="button" style={btn("#16a34a")} onClick={save} disabled={saving || dayLoading}>
            {saving ? "Saving…" : "Save Day"}
          </button>
          {savedExists && (
            <button type="button" style={btn("#dc2626")} onClick={deleteDay} disabled={saving}>
              Delete Day
            </button>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", margin: "0 0 8px" }}>
        <label style={{ display: "inline-flex", gap: 6, alignItems: "center", margin: 0 }}>
          <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} style={{ width: "auto", margin: 0 }} />
          Show hidden staff
        </label>
        <label style={{ display: "inline-flex", gap: 6, alignItems: "center", margin: 0 }}>
          <input type="checkbox" checked={showSubs} onChange={(e) => setShowSubs(e.target.checked)} style={{ width: "auto", margin: 0 }} />
          Show subcontractors
        </label>
      </div>

      <div style={{ ...box, padding: 0, overflowX: "auto" }}>
        {dayLoading ? (
          <div style={{ padding: 16 }}>Loading the day…</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
            <thead>
              <tr>
                <th style={th}>Worked</th>
                <th style={th}>Tech</th>
                <th style={th}>Hours</th>
                <th style={th}>Rate</th>
                <th style={th}>Day Pay</th>
                <th style={th}>Jobs</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {roster.map((u) => {
                const worked = Boolean(attendance[u.id]);
                const overridden =
                  overrides[u.id] !== undefined && overrides[u.id] !== null && overrides[u.id] !== "";
                const jobIds = assignments[u.id] || [];
                const flat = numberValue(u.dailyPayRate) > 0;
                return (
                  <tr key={u.id} style={{ background: worked ? "#f0fdf4" : "#fff", opacity: u.hiddenFromDailyLabor ? 0.6 : 1 }}>
                    <td style={td}>
                      <input
                        type="checkbox"
                        checked={worked}
                        onChange={() => toggleWorked(u)}
                        style={{ width: 18, height: 18, margin: 0 }}
                      />
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 700 }}>{staffName(u)}</div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        {u.position || (isSubContractor(u) ? "Sub Contractor" : "Employee")}
                      </div>
                    </td>
                    <td style={td}>
                      <input
                        type="number"
                        min="0"
                        step="0.25"
                        disabled={!worked}
                        value={worked ? hours[u.id] ?? "" : ""}
                        placeholder={String(getHoursPerDay(u))}
                        onChange={(e) => setHoursFor(u.id, e.target.value)}
                        style={inputStyle}
                      />
                      {clockHours[u.id] ? (
                        <div style={{ fontSize: 11, color: "#2563eb", marginTop: 2 }}>
                          Clock: {clockHours[u.id]} h
                        </div>
                      ) : null}
                    </td>
                    <td style={{ ...td, fontSize: 13, color: "#475569", whiteSpace: "nowrap" }}>
                      {flat ? `${money(u.dailyPayRate)} / day` : `${money(getHourlyPayRate(u))} / hr`}
                      {!flat && !getHourlyPayRate(u) && (
                        <div style={{ color: "#dc2626", fontSize: 11 }}>No rate on Staff page</div>
                      )}
                    </td>
                    <td style={td}>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        disabled={!worked}
                        value={worked ? (overridden ? overrides[u.id] : "") : ""}
                        placeholder={worked ? payFor(u).toFixed(2) : ""}
                        onChange={(e) => setOverrideFor(u.id, e.target.value)}
                        style={{ ...inputStyle, width: 110, fontWeight: 700 }}
                        title="Leave blank to use the calculated pay"
                      />
                      {worked && overridden && (
                        <button
                          type="button"
                          onClick={() => setOverrideFor(u.id, "")}
                          style={{ ...btn("transparent", "#2563eb"), padding: "2px 4px", fontSize: 11 }}
                        >
                          use calculated
                        </button>
                      )}
                    </td>
                    <td style={td}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxWidth: 360 }}>
                        {jobIds.map((id) => (
                          <span
                            key={id}
                            style={{
                              fontSize: 12,
                              background: "#e0e7ff",
                              color: "#3730a3",
                              borderRadius: 999,
                              padding: "2px 8px",
                            }}
                          >
                            {jobsById[id] ? jobLabel(jobsById[id]) : "Deleted job"}
                          </span>
                        ))}
                        {worked && !jobIds.length && (
                          <span style={{ fontSize: 12, color: "#b45309" }}>No job picked</span>
                        )}
                      </div>
                      {worked && (
                        <button
                          type="button"
                          onClick={() => {
                            setPickerSearch("");
                            setPickerFor(u.id);
                          }}
                          style={{ ...btn("#eef2ff", "#3730a3"), padding: "4px 10px", fontSize: 12, marginTop: 6 }}
                        >
                          Pick jobs
                        </button>
                      )}
                    </td>
                    <td style={td}>
                      <button
                        type="button"
                        onClick={() => toggleHidden(u)}
                        style={{ ...btn("transparent", "#64748b"), padding: "2px 4px", fontSize: 12 }}
                        title="Hide people who never do field work from this list"
                      >
                        {u.hiddenFromDailyLabor ? "Unhide" : "Hide"}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!roster.length && (
                <tr>
                  <td style={td} colSpan={7}>
                    No staff to show.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div style={box}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <h3 style={{ margin: 0 }}>Saved Tech Daily Pay</h3>
          <select
            value={summaryRange}
            onChange={(e) => setSummaryRange(e.target.value)}
            style={{ ...inputStyle, width: 150 }}
          >
            <option value="week">This week</option>
            <option value="month">This month</option>
          </select>
          <span style={{ color: "#64748b", fontSize: 13 }}>
            {moment(rangeStart).format("MMM D")} – {moment(rangeEnd).format("MMM D, YYYY")}
          </span>
          <span style={{ marginLeft: "auto", fontWeight: 900, fontSize: 18 }}>{money(periodSummary.total)}</span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
            <thead>
              <tr>
                <th style={th}>Tech</th>
                <th style={th}>Days</th>
                <th style={th}>Hours</th>
                <th style={th}>Pay</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(periodSummary.byUser)
                .sort((a, b) => b[1].amount - a[1].amount)
                .map(([uid, row]) => (
                  <tr key={uid}>
                    <td style={td}>{row.name || staffName(usersById[uid] || {}) || uid}</td>
                    <td style={td}>{row.days}</td>
                    <td style={td}>{roundHours(row.hours)}</td>
                    <td style={td}>{money(row.amount)}</td>
                  </tr>
                ))}
              {!Object.keys(periodSummary.byUser).length && (
                <tr>
                  <td style={td} colSpan={4}>
                    Nothing saved for this period yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {periodSummary.unassigned > 0 && (
          <p style={{ color: "#b45309", fontSize: 13, marginBottom: 0 }}>
            {money(periodSummary.unassigned)} of this period's pay isn't tied to a job. It's counted
            as general labor (overhead) on the Accounting pages. Pick jobs to put it on the jobs.
          </p>
        )}
      </div>

      {pickerFor &&
        pickerUser &&
        createPortal(
          <>
            <div
              onClick={() => setPickerFor(null)}
              style={{
                position: "fixed",
                inset: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.55)",
                zIndex: 999998,
              }}
            />
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                margin: 0,
                width: "min(560px, calc(100vw - 32px))",
                maxHeight: "80vh",
                overflowY: "auto",
                background: "#fff",
                padding: 20,
                borderRadius: 12,
                boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
                textAlign: "left",
                boxSizing: "border-box",
                zIndex: 999999,
              }}
            >
              <h3 style={{ marginTop: 0 }}>Jobs for {staffName(pickerUser)}</h3>
              <p style={{ marginTop: 0, color: "#64748b", fontSize: 13 }}>
                Jobs with a visit on {moment(date).format("MMM D")} are listed. Search to find any
                other job. Their day pay is split evenly over the jobs you tick.
              </p>
              <input
                type="text"
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                placeholder="Search WO, site or service…"
                style={{ ...inputStyle, width: "100%", marginBottom: 10 }}
              />
              {pickerJobs.map((j) => {
                const checked = (assignments[pickerFor] || []).includes(j.id);
                const today = jobsToday.some((x) => x.id === j.id);
                return (
                  <label
                    key={j.id}
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      padding: "6px 4px",
                      borderBottom: "1px solid #f1f5f9",
                      margin: 0,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleJob(pickerFor, j.id)}
                      style={{ width: 16, height: 16, margin: 0 }}
                    />
                    <span style={{ flex: 1 }}>
                      {jobLabel(j)}
                      {j.serviceType ? (
                        <span style={{ color: "#64748b", fontSize: 12 }}> · {j.serviceType}</span>
                      ) : null}
                    </span>
                    {today && (
                      <span style={{ fontSize: 11, color: "#166534", background: "#dcfce7", borderRadius: 999, padding: "1px 8px" }}>
                        today
                      </span>
                    )}
                  </label>
                );
              })}
              {!pickerJobs.length && <p style={{ color: "#64748b" }}>No jobs found.</p>}
              <div style={{ textAlign: "right", marginTop: 12 }}>
                <button type="button" style={btn("#2563eb")} onClick={() => setPickerFor(null)}>
                  Done
                </button>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}
